<template>

	<div class="container-fluid p-0 objectview-wrapper">
		<div class="row no-gutters header">
			<div class="col-12">
				<erco-header :name="objectName"
				             :id="objectId"
				             :breadcrumb="breadcrumb"
				             :previewUrl="objectPreviewUrl"
				             :status="objectStatus"
				             :stati="stati"
				             :isProductive="objectIsProductive">
				</erco-header>
			</div>
		</div>

		<div class="row no-gutters view-menu">
			<div class="col-12">
				<erco-menu :items="subviews" :jumpTo="jumpTo" :lang="objectLanguage"></erco-menu>
			</div>
		</div>

		<div class="row no-gutters main">
			<div class="col-12">
				<erco-main>
					<keep-alive>
						<component v-bind:is="view" v-if="objectDataset" :dataset="objectDataset"></component>
					</keep-alive>
				</erco-main>

				<transition name="sidebar">
					<erco-sidebar v-if="sidebar.visible" :sidebar="sidebar">
					</erco-sidebar>
				</transition>
			</div>

			<erco-cloak v-if="loading"></erco-cloak>
		</div>
	</div>
</template>

<script>
	import ErcoCloak from '@/components/molecules/ErcoCloak';

	import ErcoHeader from '@/components/molecules/ErcoHeader';
	import ErcoMain from '@/components/organisms/ErcoMain';
	import ErcoMenu from '@/components/atoms/ErcoMenu';

	import Setup from '@/components/organisms/ErcoObjectView/Setup';
	import ObjectData from '@/components/organisms/ErcoObjectView/ObjectData';
	import Seo from '@/components/organisms/ErcoObjectView/Seo';
	import Assets from '@/components/organisms/ErcoObjectView/Assets';
	import Layout from '@/components/organisms/ErcoObjectView/Layout';

	import ErcoSidebar from '@/components/organisms/ErcoSidebar';

	export default {
		name: 'ErcoObjectViewPage',

		created() {
			const cookie = window.document.cookie
				.split(';')
				.reduce((acc, pair) => {
					const splitPair = pair.trim().split('=');
					acc[splitPair[0]] = splitPair[1];
					return acc;
				}, {});

			const defaultLang = 'de';
			const paramLang = this.$route.query.lang;
			const cookieLang = cookie.lang;
			const userLang = this.objectLanguage
				|| paramLang
				|| cookieLang
				|| defaultLang;

			this.$store.dispatch('setObjectLanguage', userLang);
			this.$store.dispatch('resetObjectSites');
			this.$store.dispatch('getStatiList');
			this.init();
		},

		data() {
			return {
				subviews: [
					{
						name: 'setup',
						title: 'Setup',
						active: false,
					},
					{
						name: 'object-data',
						title: 'Objektdaten',
						active: false,
					},
					{
						name: 'seo',
						title: 'Seo',
						active: false,
					},
					{
						name: 'assets',
						title: 'Assets',
						active: false,
					},
					{
						name: 'layout',
						title: 'Layout',
						active: false,
					},
				],

				view: 'setup',
			};
		},

		computed: {
			objectDataset() {
				return this.$store.state.objectsetup.current;
			},

			objectId() {
				return this.objectDataset.PKM_Objekt;
			},

			objectLanguage() {
				return this.$store.state.objectsetup.language;
			},

			objectLoading() {
				return this.$store.state.objectsetup.loading;
			},

			objectName() {
				return this.objectDataset[`S_${this.objectLanguage}_Objekt`] || '';
			},

			breadcrumb() {
				const category = this.objectDataset.S_Bereich;
				const genus = this.objectDataset.S_Gattung;

				return (typeof category === 'string' &&
				        typeof genus === 'string') ?
					[category, genus] : [];
			},

			objectPreviewUrl() {
				return this.$store.getters.visitableObjectPreviewUrl;
			},

			objectStatus() {
				return this.objectDataset.FKM_Status || -1;
			},

			objectStatusWaiting() {
				return this.$store.state.objectsetup.statusWaiting;
			},

			objectIsProductive() {
				return this.$store.getters.objectIsProductive;
			},

			stati() {
				return this.$store.getters.statiAssoc;
			},

			loading() {
				return this.objectLoading ||
				       this.objectStatusWaiting ||
				       (['object-data', 'seo', 'assets'].includes(this.view)
				        && this.$store.state.objectsites.objectdata.loading) ||
				       (this.view === 'layout'
				        && this.$store.state.objectsites.sites.length === 0
				        && !this.$store.state.objectsites.sitesFinishedLoading);
			},

			/* Sidebar */

			sidebar() {
				return this.$store.state.sidebar;
			},
		},

		methods: {
			init() {
				const objectId = parseInt(this.$route.params.objectId, 10);

				const lang = this.objectLanguage;

				this.$store.dispatch('getObjectSetupWithSites', { id: objectId, lang }).then(() => {
					this.$store.dispatch('setAccessErrorStatusCode', -1);
					const title = this.objectDataset[`S_${lang}_Objekt`];
					document.title = `${objectId} - ${title}`;
				})
				.catch(() => {
					 /* TODO: Use (better) logging; 'Keine Verbindung zur Ressource:', response */
				});

				if (this.$route.params.view) {
					const view = this.$route.params.view;
					const viewExists = this.subviews.filter(subview => (subview.name === view)).length > 0;

					if (viewExists) {
						this.view = view;
					}
				} else {
					const path = this.$route.path.replace(/\/$/, '');

					const defaultRoutePath = `${path}/${this.view}`;
					this.$router.replace(defaultRoutePath);
				}

				this.jumpTo(this.view);
			},

			jumpTo(view) {
				this.closeSidebar();

				this.view = view;
				this.$router.replace(view);

				this.subviews.forEach((subview) => {
					subview.active = (subview.name === view);
				});
			},

			closeSidebar() {
				this.$store.dispatch('resetSidebar');
			},
		},

		watch: {
			/* Objekt bei Sprachwechsel neu initialisieren */
			objectLanguage(newLang, oldLang) {
				/* Nur neu initialisieren, wenn zuvor bereits schon einmal über den
					created-Hook die Initialisierungsfunktion aufgerufen wurde und der
					lang-Wert somit noch ein Leerstring war */
				if (oldLang === '') {
					return;
				}
				this.init();
			},
		},

		beforeRouteEnter(to, from, next) {
			if (from.path === '/dashboard') {
				const scrollEl = document.scrollingElement;
				scrollEl.scrollTop = 0;
			}

			next();
		},

		components: {
			ErcoCloak,

			ErcoHeader,
			ErcoMenu,
			ErcoMain,

			Setup,
			ObjectData,
			Seo,
			Assets,
			Layout,

			ErcoSidebar,
		},
	};
</script>

<style lang="scss">

	.objectview-wrapper {
		height: 100%;

		& > .header {
			height: 3.5rem;
		}

		& > .view-menu {
			height: 3.5rem;
		}

		& > .main {
			min-height: calc(100vh - 2 * 3.5rem);
			position: relative;
		}

		.sidebar-enter-active, .sidebar-leave-active {
		  transition: opacity 0.5s;
		}

		.sidebar-enter, .sidebar-leave-to {
			opacity: 0;
		}
	}

</style>



// WEBPACK FOOTER //
// ErcoObjectView.vue?0de4cfa8